import lazy from '@fatso83/retry-dynamic-import/react-lazy';
import { UserType } from 'interfaces/user';
import { FormDialogProvider } from 'providers/FormDialogProvider';
import { WhatsAppEntryProvider } from 'providers/WhatsAppEntryProvider';
import { ReactNode, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { logout } from 'services/auth';
import { getPrefixedRoute, getRoutePrefix } from 'services/theming';
import { BuyersMarketProvider } from 'screens/BuyersMarket/providers/BuyersMarketProvider';
import { InputsCheckoutProvider } from 'screens/InputsCheckout/providers/InputsCheckoutProvider';
import OperationsScreen from 'screens/Operations/OperationsScreen';
import PaymentsScreen from 'screens/Payments/PaymentsScreen';
import { ProductsProvider } from 'screens/Products/useProducts';
import { ProductsUIProvider } from 'screens/Products/useProductsUI';
import { ReportsProviders } from 'screens/Reports/ReportsProviders';
import SalesScreen from 'screens/Sales/SalesScreen';
import EditShipments from 'screens/Shipments/EditShipments';
import { DelayedFallback } from 'components/DelayedFallback';
import HomeRedirect from 'components/HomeRedirect';
import MarketRouteAccess from 'components/MarketRouteAccess';
import NetworkLoading from 'components/NetworkLoading';
import OnlineStatusAlert from 'components/OnlineStatusAlert';
import { RequiresAuth } from 'components/RequiresAuth';
import { RouteAccess } from 'components/RouteAccess';
import Screen from 'components/Screen';
import { useWhiteLabelling, WhiteLabellingProvider } from 'components/WhiteLabellingProvider';
import { paths } from 'config/paths';
import { Permission, Scope } from 'hooks/useAccess';
import { useFeatures } from 'hooks/useFeatures';
import { GlobalContentProvider } from 'hooks/useGlobalContent';
import useHeadlessUser from 'hooks/useHeadlessUser';
import useIsFreshmarkProfile from 'hooks/useIsFreshmarkProfile';
import { PackhouseProvider } from 'hooks/usePackhouse';
import { ProfileConfigProvider } from 'hooks/useProfileConfig';
import useRouteAnalytics from 'hooks/useRouteAnalytics';
import { useShipmentsOrSalesPath } from 'hooks/useShipmentsOrSalesPath';
import useUser from 'hooks/useUser';
import useRouteTracker from './hooks/useRouteTracker';

const ShipmentsOrSalesRedirect = () => {
  const path = useShipmentsOrSalesPath();
  return <Navigate to={path} replace />;
};

/**
 * /shipments is only for buyers and sellers. Admins should use /sales, /operations, or /payments.
 * Redirect admin from /shipments (and /shipments/history) to the default tab.
 */
const ShipmentsRouteGuard = ({ children }: { children: ReactNode }) => {
  const { pathname, search } = useLocation();
  const { user } = useUser();
  const shipmentsOrSalesPath = useShipmentsOrSalesPath();

  const isAdmin = user?.userType === UserType.Admin;
  // shipments/truck/:truckId/:intent is the bulk-edit detail route (EditShipments),
  // not the shipments list — exempt it so admins aren't redirected away from it.
  const isShipmentsListRoute =
    (pathname.includes('/shipments') && !pathname.includes('/shipment/') && !pathname.includes('/shipments/truck/')) ||
    pathname.includes('shipments/history');

  if (isAdmin && isShipmentsListRoute && shipmentsOrSalesPath === paths.sales()) {
    const profileID = new URLSearchParams(search).get('profileID');
    const target = profileID
      ? `${shipmentsOrSalesPath}?profileID=${encodeURIComponent(profileID)}`
      : shipmentsOrSalesPath;
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
};

const Account = lazy(() => import('screens/Accounts/Account'));
const AccountingDoc = lazy(() => import('screens/Accounts/AccountingDoc'));
const Administration = lazy(() => import('screens/Administration/Administration'));
const AdminProducts = lazy(() => import('screens/Administration/AdminProducts'));
const AdminRoutes = lazy(() => import('screens/Administration/Routes'));
const BuyerProductsNew = lazy(() => import('screens/Buyer/BuyerProductsNew'));
const BuyersMarket = lazy(() => import('screens/BuyersMarket/BuyersMarket'));
const BuyersMarketCheckout = lazy(() => import('screens/Checkout/Checkout'));
const CaseTransfers = lazy(() => import('screens/Buyer/CaseTransfers'));
const CaseTypes = lazy(() => import('screens/Administration/CaseTypes'));
const CatalogMarket = lazy(() => import('screens/CatalogMarket/CatalogMarket'));
const Chat = lazy(() => import('screens/Chat/Chat'));
const ChepLocations = lazy(() => import('screens/Administration/ChepLocations'));
const Customers = lazy(() => import('screens/Customers/Customers'));
const ProfileGroups = lazy(() => import('screens/ProfileGroups/ProfileGroups'));
const Debug = lazy(() => import('screens/Debug/Debug'));
const Discounts = lazy(() => import('screens/Administration/Discounts'));
const EditProfile = lazy(() => import('screens/UserSettings/EditProfile'));
const EditShipment = lazy(() => import('screens/Shipments/EditShipment'));
const Explore = lazy(() => import('screens/Explore/Explore'));
const Finance = lazy(() => import('screens/Finance/Finance'));
const FormScreen = lazy(() => import('screens/FormScreen'));
const FormScreenLauncher = lazy(() => import('screens/FormScreenLauncher'));
const Hubs = lazy(() => import('screens/Administration/Hubs'));
const InputsCheckout = lazy(() => import('screens/InputsCheckout/InputsCheckout'));
const InputsMarket = lazy(() => import('screens/InputsMarket/InputsMarket'));
const AccessDenied = lazy(() => import('screens/Auth/AccessDenied'));
const Landing = lazy(() => import('screens/Auth/Landing'));
const NotFound = lazy(() => import('screens/Auth/NotFound'));
const Locations = lazy(() => import('screens/Administration/Locations'));
const Login = lazy(() => import('screens/Auth/Login'));
const MarketExport = lazy(() => import('screens/Finance/MarketExport'));
const Marketing = lazy(() => import('screens/Marketing/Marketing'));
const Notifications = lazy(() => import('screens/Notifications/Notifications'));
const NotificationsDetail = lazy(() => import('screens/Notifications/NotificationsDetail'));
const Onboard = lazy(() => import('screens/UserSettings/Onboard'));
const OrderEditHistory = lazy(() => import('screens/Orders/OrderEditHistory'));
const Posts = lazy(() => import('screens/Marketing/Posts'));
const PriceMailer = lazy(() => import('screens/Marketing/PriceMailer'));
const Products = lazy(() => import('screens/Products/Products'));
const QualityReport = lazy(() => import('screens/QualityReport/QualityReport'));
const QualityReportNew = lazy(() => import('screens/QualityReport/QualityReportNew'));
const QuoteSourceDoc = lazy(() => import('screens/Quotes/QuoteSourceDoc'));
const Quotes = lazy(() => import('screens/Quotes/Quotes'));
const QuotesRequest = lazy(() => import('screens/QuotesRequest/QuotesRequest'));
const Reports = lazy(() => import('screens/Reports/Reports'));
const Packhouse = lazy(() => import('screens/Packhouse/Packhouse'));
const RouteRatesNew = lazy(() => import('screens/Administration/RouteRatesNew'));
const SageExport = lazy(() => import('screens/Finance/SageExport'));
const SeasonPlanner = lazy(() => import('screens/SeasonPlanner/SeasonPlanner'));
const SellerLocations = lazy(() => import('screens/Seller/SellerLocations'));
const SellerProductDetails = lazy(() => import('screens/Seller/SellerProductDetails'));
const SellerSetup = lazy(() => import('screens/UserSettings/SellerSetup'));
const ShipmentTransactions = lazy(() => import('screens/Shipments/ShipmentTransactions'));
const Shipments = lazy(() => import('screens/Shipments/Shipments'));
const ShipmentsHistory = lazy(() => import('screens/ShipmentsHistory/ShipmentsHistory'));
const SignUp = lazy(() => import('screens/Auth/SignUp/SignUp'));
const OrderDoc = lazy(() => import('screens/SourceDoc/OrderDoc'));
const SourceDoc = lazy(() => import('screens/SourceDoc/SourceDoc'));
const TruckTypes = lazy(() => import('screens/Administration/TruckTypes'));
const Transporters = lazy(() => import('screens/Administration/Transporters'));
const QualityDefects = lazy(() => import('screens/Administration/QualityDefects'));
const User = lazy(() => import('screens/User/User'));
const UserProfiles = lazy(() => import('screens/UserSettings/UserProfiles'));
const UserSettingsProfile = lazy(() => import('screens/UserSettings/Profile'));
const VerifyEmail = lazy(() => import('components/VerifyEmail/VerifyEmail'));
const XeroExport = lazy(() => import('screens/Finance/XeroExport'));

const LogoutRedirect = () => {
  useEffect(() => {
    logout(true);
  }, []);
  return null;
};

const SubRoutes = () => {
  useHeadlessUser();
  useRouteAnalytics();
  useRouteTracker(useLocation());

  return (
    <>
      <WhiteLabellingProvider>
        <ProfileConfigProvider>
          <GlobalContentProvider>
            <WhatsAppEntryProvider>
              <FormDialogProvider>
                <Screen>
                  <Suspense fallback={<DelayedFallback />}>
                    <WhiteLabellingRoutes />
                  </Suspense>
                </Screen>
              </FormDialogProvider>
            </WhatsAppEntryProvider>
          </GlobalContentProvider>
        </ProfileConfigProvider>
      </WhiteLabellingProvider>

      <OnlineStatusAlert />
    </>
  );
};

const WhiteLabellingRoutes = () => {
  const isSeasonPlannerEnabled = useIsFreshmarkProfile();
  const { isInputsType, isNileFreshProfile } = useWhiteLabelling();

  const prefix = getRoutePrefix();
  const { user } = useUser();

  const { isEnabled } = useFeatures();
  const isConsolidatedInputsEnabled = isEnabled('ConsolidatedInputs');
  const isFlexibleCreditNotesEnabled = isEnabled('FlexibleCreditNotes');
  const isWarehouseQualityControlEnabled = isEnabled('WarehouseQualityControl');
  const isProfileGroupsEnabled = isEnabled('ProfileGroups');

  return (
    <Routes>
      <Route path={`${prefix}`}>
        <Route index element={<Landing />} />
        <Route path={`login`} element={<Login />} />
        <Route path={`logout`} element={<LogoutRedirect />} />
        <Route path={`signup`} element={<SignUp />} />
        <Route path={`access-denied`} element={<AccessDenied />} />

        <Route element={<RequiresAuth />}>
          <Route path={`orderDoc`} element={<OrderDoc />} />
          <Route path={`sourceDoc`} element={<SourceDoc />} />
          <Route path={`quoteDoc`} element={<QuoteSourceDoc />} />
          <Route path={`accountingDoc`} element={<AccountingDoc />} />
          <Route path={`pallets`} element={<CaseTransfers />} />
          <Route
            path={`quality-report/:id/:type/:printerFriendly`}
            element={isWarehouseQualityControlEnabled ? <QualityReportNew /> : <QualityReport />}
          />
        </Route>
        <Route element={<RequiresAuth allowHeadless />}>
          <Route
            path={'reports'}
            element={
              // TODO: Add RouteAccess permission={Permission.Reports} scope={Scope.Read}.
              <ReportsProviders>
                <Reports />
              </ReportsProviders>
            }
          />
        </Route>
        <Route element={<RequiresAuth />} errorElement={<HomeRedirect />}>
          <Route path={`index.html`} element={<HomeRedirect />} />

          <Route element={!user?.status ? <NetworkLoading /> : <Outlet />}>
            <Route path={`verify-email`} element={<VerifyEmail />} />
            <Route
              path={`seller-locations`}
              element={
                <RouteAccess permission={Permission.SellerLogistics} scope={Scope.Read}>
                  <SellerLocations />
                </RouteAccess>
              }
            />
            <Route path={`hubs`} element={<Hubs />} />
            <Route path={`chat`} element={<Chat />} />
            <Route path={`locations`} element={<Locations />} />
            <Route
              path={`chep-locations`}
              element={
                <RouteAccess permission={Permission.CHEPLocations} scope={Scope.Read}>
                  <ChepLocations />
                </RouteAccess>
              }
            />
            {/*
              ConsolidatedInputs splits the one /checkout route in two: the cart
              checkout gets /cart and /checkout is the quotes checkout alone, so
              a buyer can reach both once inputs products sell through the fresh
              market. Off, /checkout serves whichever one the profile's market
              type implies and /cart is only a redirect onto it, keeping every
              existing link working.
            */}
            <Route
              path={`cart`}
              element={
                isConsolidatedInputsEnabled ? (
                  <BuyersMarketProvider>
                    <BuyersMarketCheckout />
                  </BuyersMarketProvider>
                ) : (
                  <Navigate to={paths.checkout()} replace />
                )
              }
            />
            <Route
              path={`checkout`}
              element={
                isInputsType || isConsolidatedInputsEnabled ? (
                  <InputsCheckoutProvider>
                    <InputsCheckout />
                  </InputsCheckoutProvider>
                ) : (
                  <BuyersMarketProvider>
                    <BuyersMarketCheckout />
                  </BuyersMarketProvider>
                )
              }
            />
            <Route element={<RouteAccess permission={Permission.Market} scope={Scope.Read} />}>
              <Route element={<MarketRouteAccess path={'market'} />}>
                <Route
                  path={`market`}
                  element={
                    <BuyersMarketProvider>{isInputsType ? <InputsMarket /> : <BuyersMarket />}</BuyersMarketProvider>
                  }
                />
              </Route>

              <Route element={<MarketRouteAccess path={'catalog-market'} />}>
                <Route
                  path={`catalog-market`}
                  element={
                    isNileFreshProfile ? (
                      <BuyersMarketProvider>
                        <CatalogMarket />
                      </BuyersMarketProvider>
                    ) : (
                      <Navigate to={getPrefixedRoute('/market')} replace />
                    )
                  }
                />
              </Route>
            </Route>
            <Route
              path={`quotes`}
              element={
                <RouteAccess permission={Permission.Market} scope={Scope.Read}>
                  <Quotes />
                </RouteAccess>
              }
            />
            <Route path={`quote/:id`} element={<QuotesRequest />} />
            <Route path={`seller-product-details`} element={<SellerProductDetails />} />
            <Route path={`discounts`} element={<Discounts />} />
            <Route path={`dashboard-posts`} element={<Posts />} />
            <Route path={`admin/products`} element={<AdminProducts />} />
            <Route path={`buyer/products`} element={<BuyerProductsNew />} />
            <Route path={`route-rates`} element={<RouteRatesNew />} />
            <Route path={`routes`} element={<AdminRoutes />} />
            <Route path={`case-types`} element={<CaseTypes />} />
            <Route path={`truck-types`} element={<TruckTypes />} />
            <Route path={`transporters`} element={<Transporters />} />
            {isWarehouseQualityControlEnabled && (
              <Route
                path={`quality-defects`}
                element={
                  <RouteAccess permission={Permission.ProductConfig} scope={Scope.Read}>
                    <QualityDefects />
                  </RouteAccess>
                }
              />
            )}
            <Route path={`market-export`} element={<MarketExport />} />
            <Route path={`xero-export`} element={<XeroExport />} />
            <Route path={`sage-export`} element={<SageExport />} />
            <Route
              path={`seller-setup`}
              element={
                <RouteAccess permission={Permission.Setup} scope={Scope.Read}>
                  <SellerSetup />
                </RouteAccess>
              }
            />
            <Route path={`profile/administration`} element={<Administration />} />
            <Route
              path={`profile/marketing`}
              element={
                <RouteAccess permission={Permission.MarketingTools} scope={Scope.Read}>
                  <Marketing />
                </RouteAccess>
              }
            />
            <Route
              path={`profile/finance`}
              element={
                <RouteAccess permission={Permission.Finance} scope={Scope.Read}>
                  <Finance />
                </RouteAccess>
              }
            />
            <Route path={`profile/user-profiles`} element={<UserProfiles />} />
            <Route path={`profile/user`} element={<User />} />
            <Route
              path={`catalog/products`}
              element={
                <ProductsProvider>
                  <ProductsUIProvider>
                    <Products />
                  </ProductsUIProvider>
                </ProductsProvider>
              }
            />
            <Route
              path={`packhouse`}
              element={
                <RouteAccess permission={Permission.Packhouse} scope={Scope.Read}>
                  <PackhouseProvider>
                    <Packhouse />
                  </PackhouseProvider>
                </RouteAccess>
              }
            />
            <Route path={`profile/:id/:type`} element={<FormScreen />} />
            <Route path={`explore`} element={<Explore />} />
            <Route path={`shipment/history/:id`} element={<OrderEditHistory />} />
            <Route path={`new/shipment-transactions/:shipmentId`} element={<ShipmentTransactions />} />
            <Route path={`form`} element={isFlexibleCreditNotesEnabled ? <FormScreenLauncher /> : <FormScreen />} />
            <Route
              path={`quality-report/:id/:type`}
              element={isWarehouseQualityControlEnabled ? <QualityReportNew /> : <QualityReport />}
            />
            <Route path={`supply`} element={<Navigate to={`seller/products`} replace />} />
            <Route path={`pricing`} element={<Navigate to={`seller/products`} replace />} />
            <Route
              path={`seller/products`}
              element={
                <ProductsProvider>
                  <ProductsUIProvider>
                    <Products />
                  </ProductsUIProvider>
                </ProductsProvider>
              }
            />
            {isSeasonPlannerEnabled && <Route path={`season-planner`} element={<SeasonPlanner />} />}
            <Route path={`new/account`} element={<Account />} />
            <Route path={`edit/profile/:profileId`} element={<EditProfile />} />
            <Route path={`new/profile`} element={<UserSettingsProfile />} />
            <Route path={`price-mailer`} element={<PriceMailer />} />
            <Route
              path={`customers`}
              element={
                <RouteAccess permission={Permission.Customers} scope={Scope.Read}>
                  <Customers />
                </RouteAccess>
              }
            />
            <Route
              path={`users`}
              element={
                <RouteAccess permission={Permission.Customers} scope={Scope.Read}>
                  <Customers />
                </RouteAccess>
              }
            />
            {isProfileGroupsEnabled && (
              <Route
                path={`profile-groups`}
                element={
                  <RouteAccess permission={Permission.Customers} scope={Scope.Read}>
                    <ProfileGroups />
                  </RouteAccess>
                }
              />
            )}
            <Route path={`onboard`} element={<Onboard />} />
            <Route path={`new`} element={<ShipmentsOrSalesRedirect />} />
            <Route path={`notifications/*`} element={<Notifications />} />
            <Route path={`notifications/:type`} element={<NotificationsDetail />} />
            <Route path={`debug`} element={<Debug />} />

            {/* Sales */}
            <Route
              path={`sales`}
              element={
                <RouteAccess permission={Permission.Sales} scope={Scope.Read}>
                  <SalesScreen />
                </RouteAccess>
              }
            />

            {/* Operations */}
            <Route
              path={`operations`}
              element={
                <RouteAccess permission={Permission.Operations} scope={Scope.Read}>
                  <OperationsScreen />
                </RouteAccess>
              }
            />

            {/* Payments */}
            <Route
              path={`payments`}
              element={
                <RouteAccess permission={Permission.Finance} scope={Scope.Read}>
                  <PaymentsScreen />
                </RouteAccess>
              }
            />

            <Route
              element={
                <ShipmentsRouteGuard>
                  <Outlet />
                </ShipmentsRouteGuard>
              }
            >
              <Route path={`shipments`} element={<Shipments />} />
              <Route path={`shipments/history`} element={<ShipmentsHistory />} />
              <Route path={`shipment/inbound/:id/:intent?`} element={<EditShipment />} />
              <Route path={`shipment/outbound/:id/:intent?`} element={<EditShipment />} />
              <Route path={`shipment/finance/:id/:intent?`} element={<EditShipment />} />
              <Route path={`shipment/direct/:id/:intent?`} element={<EditShipment />} />
              <Route path={`shipment/documents/:id/:intent?`} element={<EditShipment />} />
              <Route path={`shipment/upcoming/:id/:intent?`} element={<EditShipment />} />
              <Route path={`shipment/:id/:intent?`} element={<EditShipment />} />
              <Route path={`shipments/truck/:truckId/:intent?`} element={<EditShipments />} />
            </Route>
          </Route>
        </Route>
        <Route path="404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default SubRoutes;
